import retargetingAttributionRepository from '@/repository/generatedRepository/retargetingAttributionRepository'
import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { canFitInLocalStorage } from '@/helpers/reports'
import { defaultFilters, sortReportRows, GROUP_COL, SECOND_GROUP_COL } from '@/models/RetargetingAttribution/helpers'

const ids = items => (items || []).map(item => item.id)

// CPA-3455: the report request body; multiselects are kept in filters as {id, title} objects
function toReportPayload(filter) {
  return {
    dateFrom: filter.dateFrom,
    dateTo: filter.dateTo,
    groupBy: filter.groupBy,
    secondGroupBy: filter.secondGroupBy || '',
    audiences: ids(filter.audiences),
    advertisers: ids(filter.advertisers),
    partners: ids(filter.partners),
    offers: ids(filter.offers),
    segments: filter.segments || [],
    cohorts: filter.cohorts || [],
    statuses: filter.statuses || [],
  }
}

// g1/g2 are the first and second group value columns; ids keep opened groups stable across sorting
function prepareRows(rows) {
  return rows.map(row => {
    const id = `r-${row.key}`
    return {
      ...row,
      id,
      [GROUP_COL]: row.title,
      [SECOND_GROUP_COL]: '',
      ...(row.sg
        ? {
            sg: row.sg.map(child => ({
              ...child,
              id: `${id}-${child.key}`,
              [GROUP_COL]: '',
              [SECOND_GROUP_COL]: child.title,
            })),
          }
        : {}),
    }
  })
}

const state = {
  filters: StorageService.get(storageKeys.RETARGETING_ATTRIBUTION_FILTERS) || defaultFilters(),
  list: [],
  total: {},
  reportFilter: null,
}

const getters = {
  filters: state => state.filters,
  total: state => state.total,
  // Filter the shown report was built with (export file name, second group column)
  reportFilter: state => state.reportFilter,
  isOpenFilterPanel: state => !!state.filters.clientOptions?.filterPanelExpanded,
  listSorted: state => sortReportRows(state.list, { sortBy: state.filters.sortBy, sortDesc: state.filters.sortDesc }),
}

const actions = {
  async loadReport({ commit }, { signal, ...filters }) {
    commit('setFilters', filters)
    StorageService.set(storageKeys.RETARGETING_ATTRIBUTION_FILTERS, filters)

    const { data } = await retargetingAttributionRepository.report({ ...toReportPayload(filters.filter), signal })

    if (canFitInLocalStorage(data)) {
      StorageService.set(storageKeys.RETARGETING_ATTRIBUTION_DATA, data)
    } else {
      StorageService.remove(storageKeys.RETARGETING_ATTRIBUTION_DATA)
    }
    commit('setReport', { data, filter: filters.filter })
  },
  // Player card "Retargeting clicks" block (ADMIN only): newest clicks first, paginated
  async loadPlayerAttributions(context, { playerId, pagination }) {
    const { data } = await retargetingAttributionRepository.list({ player_id: playerId, pagination })
    return data
  },
  async loadSegments(context, { dateFrom, dateTo }) {
    const { data } = await retargetingAttributionRepository.segments({ dateFrom, dateTo })
    return data
  },
  updateFilters({ commit, getters }, patch) {
    const updatedFilters = { ...getters.filters, ...patch }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.RETARGETING_ATTRIBUTION_FILTERS, updatedFilters)
  },
  // Shows the last built report from storage, like GR does on page open
  restoreReport({ commit, state }) {
    const data = StorageService.get(storageKeys.RETARGETING_ATTRIBUTION_DATA)
    if (data) commit('setReport', { data, filter: state.filters.filter })
    return !!data
  },
  filtersStorageReset({ commit, state }) {
    StorageService.remove(storageKeys.RETARGETING_ATTRIBUTION_FILTERS)
    StorageService.remove(storageKeys.RETARGETING_ATTRIBUTION_DATA)
    commit('setFilters', { ...defaultFilters(), clientOptions: state.filters.clientOptions })
    commit('setReport', { data: null })
  },
}

const mutations = {
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  setReport(state, { data, filter = null }) {
    state.list = data ? prepareRows(data.rows || []) : []
    state.total = data?.total || {}
    state.reportFilter = data ? filter : null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
